import React, { useEffect, useState } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  BooleanInput,
  DateInput,
  ReferenceInput,
  SelectInput,
  ArrayInput,
  SimpleFormIterator,
  useDataProvider,
  useCreate,
  useNotify,
  useRedirect,
  Loading,
  SaveButton,
  Toolbar
} from 'react-admin';
import { Box, Button } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { EntityMetadata, EntityFieldMetadata } from '../../services/types/entity-discovery.types';
import { JSONSchemaForm } from '../form/json.schema.form';
import { RJSFSchema } from '@rjsf/utils';
import { apiClient } from '../../framework/api/api.client';
import { TopToolbar, ListButton } from 'react-admin';

interface DynamicEntityCreateProps {
  entityMetadata: EntityMetadata;
  schemaKey?: string; // Optional: if provided, will fetch editor schema from backend
}

/**
 * Enhanced Dynamic Entity Create
 * 
 * Behavior:
 * 1. If schemaKey is provided: Fetch editor schema from backend and use JSONSchemaForm
 * 2. Fallback: Use traditional field-by-field rendering
 */
export const DynamicEntityCreateV2: React.FC<DynamicEntityCreateProps> = ({ 
  entityMetadata, 
  schemaKey 
}) => {
  console.log('🎨 DynamicEntityCreateV2 mounted!', { 
    entityName: entityMetadata?.name,
    schemaKey,
    hasSchemaKey: !!schemaKey
  });

  const dataProvider = useDataProvider();
  const [editorSchema, setEditorSchema] = useState<RJSFSchema | null>(null);
  const [uiSchema, setUISchema] = useState<Record<string, any> | undefined>(undefined);
  const [loading, setLoading] = useState(!!schemaKey);
  const [error, setError] = useState<string | null>(null);

  // Fetch editor schema if schemaKey is provided
  useEffect(() => {
    console.log('📋 useEffect triggered', { schemaKey, hasDataProvider: !!dataProvider });
    
    if (!schemaKey) {
      console.log('⚠️ No schemaKey provided - skipping fetch, will use traditional editor');
      setLoading(false);
      return;
    }

    const fetchEditorSchema = async () => {
      try {
        setLoading(true);
        console.log(`🔍 Fetching editor schema for CREATE: ${schemaKey}`);
        
        // Use dedicated schema-by-key endpoint (simpler and more reliable)
        // apiClient handles auth token automatically
        const schema = await apiClient.get(`/schema/key/${schemaKey}`);
        
        if (schema) {
          console.log(`✅ Found schema: ${schema.name} (key: ${schema.key})`);
          console.log(`   Has editorSchema: ${!!schema.editorSchema}`);
          
          if (schema.editorSchema) {
            const parsedSchema = typeof schema.editorSchema === 'string' 
              ? JSON.parse(schema.editorSchema)
              : schema.editorSchema;
            
            console.log(`   Parsed editorSchema with ${Object.keys(parsedSchema.properties || {}).length} fields`);
            setEditorSchema(parsedSchema);
            
            // Check if there's a uiSchema in metadata
            if (schema.configuration?.metadata?.uiSchema) {
              setUISchema(schema.configuration.metadata.uiSchema);
            }
          } else {
            console.warn(`⚠️ Schema ${schemaKey} has no editorSchema - falling back to traditional editor`);
          }
        } else {
          console.warn(`⚠️ Schema not found for key: ${schemaKey} - falling back to traditional editor`);
        }
      } catch (err) {
        console.error('❌ Failed to fetch editor schema:', err);
        setError('Failed to load editor configuration');
      } finally {
        setLoading(false);
      }
    };

    fetchEditorSchema();
  }, [schemaKey, dataProvider]);

  console.log('🎯 Render decision (CREATE):', {
    loading,
    hasEditorSchema: !!editorSchema,
    editorSchemaType: editorSchema ? typeof editorSchema : 'null',
    editorSchemaFieldCount: editorSchema?.properties ? Object.keys(editorSchema.properties).length : 0,
    willUseJSONSchemaForm: !!editorSchema && !loading
  });

  const CreateActions = () => (
    <TopToolbar>
      <ListButton />
    </TopToolbar>
  );

  if (loading) {
    console.log('⏳ Showing loading state...');
    return (
      <Create title={`Create ${entityMetadata.name}`} actions={<CreateActions />}>
        <Loading />
      </Create>
    );
  }

  // If we have an editor schema, use JSONSchemaForm
  if (editorSchema) {
    console.log('✨ Using JSONSchemaFormCreator!', {
      fieldsCount: Object.keys(editorSchema.properties || {}).length,
      hasUISchema: !!uiSchema
    });
    return <JSONSchemaFormCreator schema={editorSchema} uiSchema={uiSchema} entityName={entityMetadata.name} actions={<CreateActions />} />;
  }

  // Fallback to traditional field rendering
  console.log('⚙️ Falling back to TraditionalFieldCreator');
  return <TraditionalFieldCreator entityMetadata={entityMetadata} actions={<CreateActions />} />;
};

/**
 * JSON Schema Form based creator
 */
const JSONSchemaFormCreator: React.FC<{ 
  schema: RJSFSchema; 
  uiSchema?: Record<string, any>; 
  entityName: string;
  actions?: JSX.Element;
}> = ({ schema, uiSchema, entityName, actions }) => {
  const [formData, setFormData] = React.useState<Record<string, any>>({});
  const [create, { isLoading }] = useCreate();
  const notify = useNotify();
  const redirect = useRedirect();
  
  // Extract resource name from the URL (e.g., /consumer/create → 'consumer')
  const pathParts = window.location.pathname.split('/').filter(p => p);
  const resource = pathParts[0] || '';  // First segment is the resource name

  console.log('📝 JSONSchemaFormCreator rendering!', {
    entityName,
    resource,
    schemaProperties: Object.keys(schema.properties || {}),
    hasUISchema: !!uiSchema,
    formDataKeys: Object.keys(formData)
  });

  const handleOnChange = (data: Record<string, any>) => {
    console.log('💾 JSONSchemaForm onChange (CREATE):', Object.keys(data));
    setFormData(data);
  };

  const handleSave = async () => {
    console.log('💾 Saving new entity:', { resource, formData });
    try {
      await create(
        resource,
        { data: formData },
        {
          onSuccess: (data) => {
            notify(`${entityName} created successfully`, { type: 'success' });
            redirect('list', resource);
          },
          onError: (error: any) => {
            notify(`Error: ${error.message}`, { type: 'error' });
          }
        }
      );
    } catch (err: any) {
      console.error('❌ Failed to create:', err);
      notify(`Error: ${err.message}`, { type: 'error' });
    }
  };

  return (
    <Create title={`Create ${entityName}`} actions={actions || false}>
      <Box sx={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
        <JSONSchemaForm
          schema={schema}
          formData={formData}
          uiSchema={uiSchema || {}}
          onChange={handleOnChange}
        />
        <Box sx={{ marginTop: 3, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => redirect('list', resource)}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Create>
  );
};

/**
 * Traditional field-by-field creator (fallback)
 */
const TraditionalFieldCreator: React.FC<{ 
  entityMetadata: EntityMetadata;
  actions?: JSX.Element;
}> = ({ entityMetadata, actions }) => {
  console.log('🔧 TraditionalFieldCreator rendering (fallback mode)', {
    entityName: entityMetadata.name,
    totalFields: entityMetadata.fields?.length || 0,
    fieldNames: entityMetadata.fields?.map(f => f.name) || []
  });

  const renderInput = (field: EntityFieldMetadata) => {
    // Skip read-only fields
    if (field.name === '_id' || field.name === 'createdAt' || field.name === 'updatedAt') {
      return null;
    }
    // Skip framework fields
    if (field.name === 'context' || field.name === 'derivedBy' || field.name === '__v') {
      return null;
    }

    const commonProps = {
      key: field.name,
      source: field.name,
      label: formatFieldLabel(field.name),
      required: field.required
    };

    // Handle arrays
    if (field.isArray) {
      return (
        <ArrayInput {...commonProps}>
          <SimpleFormIterator>
            {renderSingleInput(field, { ...commonProps, source: '' })}
          </SimpleFormIterator>
        </ArrayInput>
      );
    }

    return renderSingleInput(field, commonProps);
  };

  const renderSingleInput = (field: EntityFieldMetadata, props: any) => {
    // Handle enums
    if (field.enum && field.enum.length > 0) {
      const choices = field.enum.map(value => ({ id: value, name: value }));
      return <SelectInput {...props} choices={choices} />;
    }

    // Handle references
    if (field.ref) {
      return (
        <ReferenceInput 
          {...props} 
          reference={field.ref.toLowerCase()}
        >
          <SelectInput optionText="name" />
        </ReferenceInput>
      );
    }

    // Handle different field types
    switch (field.type.toLowerCase()) {
      case 'number':
      case 'decimal128':
        return <NumberInput {...props} />;
      
      case 'boolean':
        return <BooleanInput {...props} />;
      
      case 'date':
        return <DateInput {...props} />;
      
      case 'string':
      default:
        if (field.name.toLowerCase().includes('description')) {
          return <TextInput {...props} multiline rows={3} />;
        }
        return <TextInput {...props} />;
    }
  };

  // Get fields for create form
  const getCreateFields = () => {
    const excludedFields = ['_id', 'id', 'createdAt', 'updatedAt', '__v', 'context', 'derivedBy'];
    return entityMetadata.fields.filter(field => 
      !excludedFields.includes(field.name)
    );
  };

  const fieldsToShow = getCreateFields();

  return (
    <Create title={`Create ${entityMetadata.name}`} actions={actions || false}>
      <SimpleForm>
        {fieldsToShow.map(field => renderInput(field))}
      </SimpleForm>
    </Create>
  );
};

/**
 * Convert field name from camelCase to human readable format
 */
function formatFieldLabel(fieldName: string): string {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

